import { TELEGRAM_OFFERS_URL, YANDEX_METRIKA_ID } from './constants';

declare global {
  interface Window {
    ym?: (id: number, action: string, ...args: unknown[]) => void;
  }
}

const MAX_STACK_LENGTH = 500;

/**
 * Размещение CTA. Значения перечислены, а не свободная строка: по #94 клики со
 * всех CTA страницы иначе сливаются в один, и сравнить размещения нельзя.
 *
 * `hero`/`about`/`offers` — лендинг. `seasonal-*` — страницы раздела
 * `/where-to-go/` (#45), где CTA обязан стоять не меньше двух раз: внутри блока
 * билетов (он показывает CTA даже при пустой витрине, #90) и в карточке в конце
 * страницы. `seasonal-inline` — CTA внутри текста там, где блока билетов нет.
 * `seasonal-disclaimer` — ссылка на канал в дисклеймере под карточками
 * направлений. `footer` — ссылки на каналы в подвале (на всех страницах).
 *
 * `home-disclaimer` — дисклеймер под карточками направлений на главной (#144).
 * Своё значение, а не `seasonal-disclaimer`: блок один и тот же, но главная —
 * самая посещаемая страница сайта, и сливать её клики с сезонными нельзя.
 *
 * `guide-inline` — CTA внутри текста гайда `/how-to-choose/` (#117). Своё
 * значение, а не `seasonal-inline`: это первая страница вне сезонного кластера,
 * и вопрос «приносит ли гайд подписки» имеет смысл только пока его клики не
 * слиты с кликами хаба. Группа `seasonal-*` при этом не переименована в
 * `content-*` — переименование разорвало бы ряд в Метрике на границе релиза.
 *
 * `route-inline` — CTA внутри текста авторского маршрута (#128). Опять своё, по
 * той же причине: маршрут отвечает на другой запрос, чем гайд, и слить их клики
 * значило бы потерять единственный способ понять, окупается ли перенос статей
 * из Дзена.
 *
 * `visa-inline` — CTA в конце статьи о визе в Испанию (#246). Отдельно от
 * `guide-inline`: страница — ребёнок гайда, но читатель другой — он уже решил,
 * куда едет, и вопрос только в том, конвертирует ли визовый интент в подписку.
 */
export type CtaPlace =
  | 'hero'
  | 'about'
  | 'offers'
  | 'seasonal-inline'
  | 'seasonal-tickets'
  | 'seasonal-end'
  | 'seasonal-disclaimer'
  | 'home-disclaimer'
  | 'guide-inline'
  | 'route-inline'
  | 'visa-inline'
  | 'footer';

type WebVitalsMetric = {
  name: string;
  value: number;
  rating: string;
};

function getMetrika(): NonNullable<Window['ym']> | null {
  if (typeof window === 'undefined' || typeof window.ym !== 'function') {
    return null;
  }

  return window.ym;
}

/**
 * Технические события уходят параметрами визита, а не целями: цель — это только действие
 * пользователя. Иначе конверсия по техническому событию близка к 100% и отчёт "Конверсии"
 * (и сравнение страниц по нему) читать невозможно.
 */
function sendParams(params: Record<string, unknown>) {
  getMetrika()?.(YANDEX_METRIKA_ID, 'params', params);
}

/** Цель Метрики. Не бросает наружу: клик по ссылке не должен ломаться из-за аналитики. */
function reachGoal(target: string, params: Record<string, unknown>) {
  try {
    getMetrika()?.(YANDEX_METRIKA_ID, 'reachGoal', target, params);
  } catch {
    // Метрика заблокирована расширением или сломалась — переход по ссылке важнее.
  }
}

/**
 * Где стоит ссылка на внутренний раздел. Значения перечислены по той же причине,
 * что и `CtaPlace` (#94): иначе переходы со всех блоков страницы сливаются в один
 * и сравнить блоки нельзя.
 *
 * Блоки главной после переработки (#230): `home-pick-card` — карточка из
 * результата выбора, `home-pick-compare`/`home-pick-prepare` — короткие входы
 * над списком результатов для тех, кому подборка уже не нужна,
 * `home-pick-empty` — альтернативы в состоянии «нечего показать». Дальше идёт
 * единый блок «Что дальше»: `home-compare` — карточка сравнения курортов,
 * `home-prepare-guide` — гайд подготовки, `home-prepare-routes` — авторский
 * маршрут, `home-routes-section` — раздел авторских маршрутов рядом с ним,
 * `home-months` — раздел с подборками по месяцам.
 *
 * Группа `home-task-*` ушла вместе с блоком «С чего начать»: он слился с
 * блоком сравнений в один, и три входа «по задаче читателя» превратились в
 * четыре ссылки на конкретные материалы. Ряд в Метрике по ним обрывается — но
 * обрывается вместе с местом, которое он измерял, а не из-за переименования.
 *
 * `home-season-more` пережил переработку намеренно: ссылка «смотреть все
 * направления» осталась на том же месте и в той же роли — из подборки на главной
 * в сезонную страницу, — и переименование разорвало бы ряд в Метрике ровно там,
 * где сравнение «до/после» и нужно. А `home-season-lead` исчез вместе со своим
 * блоком: ссылки внутри вводного абзаца в новой раскладке нет.
 */
export type SectionPlace =
  | 'home-season-more'
  | 'home-pick-card'
  | 'home-pick-compare'
  | 'home-pick-prepare'
  | 'home-pick-empty'
  | 'home-compare'
  | 'home-prepare-guide'
  | 'home-prepare-routes'
  | 'home-months'
  | 'home-routes-section'
  | 'about-route'
  | 'about-guide';

/**
 * Переход в раздел сайта (#144).
 *
 * Отдельная цель, а не просмотр страницы: просмотр говорит, что человек оказался
 * в разделе, но не говорит, откуда он туда пошёл. Вопрос задачи — работает ли
 * блок на главной, — отвечается только вторым.
 *
 * `details` — необязательные параметры конкретного размещения. Заведены под
 * карточки результатов: пять карточек ноябрьского выбора ведут на одну и ту же
 * страницу, и без `destination_slug` клик по Пхукету и клик по Хургаде
 * неразличимы — общая конверсия блока считается, а какое направление
 * заинтересовало читателя, нет. `period` и `scenario` едут туда же, потому что
 * при выборе по умолчанию события `home_pick` не было вовсе и связать переход
 * с показанной подборкой больше нечем.
 *
 * Имена ключей не пересекаются с `place`/`path` — перетереть их параметром
 * нельзя: спред стоит первым.
 */
export function trackSectionClick(
  place: SectionPlace,
  path: string,
  details?: Record<string, string>,
) {
  reachGoal('section_click', { ...details, place, path });
}

/**
 * Выбор месяца и формата отдыха на главной (#230).
 *
 * Отдельная цель, а не параметр визита: это действие пользователя, и вопрос
 * задачи — «пользуются ли выбором вообще» — отвечается только его частотой.
 *
 * Считается один раз на реальную смену значения. Нажатие уже выбранного
 * переключателя события не даёт: иначе повторные клики по активной кнопке
 * надули бы цель, а по #230 нажатие фильтра и так не приравнивается к полезному
 * переходу — тем более к нескольким.
 */
export function trackHomePick(period: string, scenario: string) {
  reachGoal('home_pick', { period, scenario });
}

export function reportClientError(error: Error & { digest?: string }) {
  sendParams({
    client_error: {
      message: error.message,
      digest: error.digest,
      stack: error.stack?.slice(0, MAX_STACK_LENGTH),
    },
  });
}

export function reportWebVital(metric: WebVitalsMetric) {
  sendParams({
    web_vitals: {
      // Ключ по имени метрики: за визит приходит до пяти метрик, иначе они перетирают друг друга.
      [metric.name]: {
        value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
        rating: metric.rating,
      },
    },
  });
}

/**
 * Главная конверсия продукта — уход в Telegram. Канал назначения (`href`) определяет цель:
 * основной канал и TripTomorrowOffers — разные продукты, сравнивать их конверсию удобнее как
 * отдельные top-level цели, а не сегментом по параметру. `place` различает размещения CTA внутри
 * одной страницы, `page` — саму страницу, чтобы сравнивать сезонные кластеры между собой.
 * Обе цели должны быть заведены в интерфейсе Метрики (см. README, "Аналитика").
 */
export function trackTgSubscribeClick(place: CtaPlace, href?: string) {
  const goal = href === TELEGRAM_OFFERS_URL ? 'tg_offers_subscribe_click' : 'tg_subscribe_click';

  reachGoal(goal, {
    place,
    page: typeof window === 'undefined' ? '' : window.location.pathname,
  });
}
